import { AppValidator } from "@/common/app-validate.ts";
import ManageContentLayout from "@/component/common/layout/ManageContentLayout.tsx";
import CustomRequiredMarkAntdForm from "@/component/CustomRequiredMarkAntdForm.tsx";
import { REGEX, ROLE_ENUM } from "@/constant/constants.ts";
import { MANAGER_PATH } from "@/constant/url-path.ts";
import { UserService } from "@/services/user/UserService.ts";
import { toast } from "@/utils/toast.tsx";
import { Button, Col, Form, Input, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const AddUserPage = ({ isEdit = false }: { isEdit?: boolean }) => {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const validators = AppValidator(t);

  const [userInfoForm] = Form.useForm();
  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get("userId")
    ? Number(queryParams.get("userId"))
    : null;

  useEffect(() => {
    async function getDetailUser() {
      if (userId && !isNaN(userId)) {
        const res: UserDetail | null | undefined =
          await UserService.getDetail(userId);
        if (res) {
          userInfoForm.setFieldsValue({ ...res });
        }
      }
    }

    if (isEdit) {
      getDetailUser().then();
    }
  }, [userId]);

  const onSubmit = (values: CreateUserRequest) => {
    const { confirmPassword, ...payload } = values as CreateUserRequest;
    if (isEdit && !payload.password) {
      delete payload.password;
    }
    if (isEdit) {
      onEdit(payload).then();
    } else {
      onSave(payload).then();
    }
  };

  const onSave = async (values: CreateUserRequest) => {
    const res: SuccessResponse | null | undefined =
      await UserService.create(values);
    if (res) {
      navigation(MANAGER_PATH.USER.LIST);
      toast.success(t("message.createNewUserSuccessful"));
    }
  };

  const onEdit = async (values: CreateUserRequest) => {
    if (userId) {
      const res: SuccessResponse | null | undefined = await UserService.update(
        userId,
        values,
      );
      if (res) {
        navigation(MANAGER_PATH.USER.LIST);
        toast.success(t("message.successfullyUpdated"));
      }
    }
  };

  const onCancel = () => {
    navigation(MANAGER_PATH.USER.LIST);
  };

  return (
    <>
      <ManageContentLayout headerFunction={<></>}>
        <div
          className={"p-5 rounded-lg border bg-white"}
          style={{ minHeight: "753px" }}
        >
          <Form
            form={userInfoForm}
            name="user-info-form"
            layout={"vertical"}
            onFinish={onSubmit}
            requiredMark={CustomRequiredMarkAntdForm}
            size={"large"}
            initialValues={{ role: ROLE_ENUM.USER }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="firstName"
                  label={t("field.firstName")}
                  rules={[validators.required(t("field.firstName"))]}
                >
                  <Input
                    max={45}
                    placeholder={t("placeholder.enterYourFirstName")}
                  />
                </Form.Item>
                <Form.Item
                  name="lastName"
                  label={t("field.lastName")}
                  rules={[validators.required(t("field.lastName"))]}
                >
                  <Input
                    max={45}
                    placeholder={t("placeholder.enterYourLastName")}
                  />
                </Form.Item>
                <Form.Item
                  name="username"
                  label={t("field.username")}
                  rules={[validators.required(t("field.username"))]}
                >
                  <Input
                    max={100}
                    placeholder={t("placeholder.enterYourUsername")}
                  />
                </Form.Item>
                {!isEdit && (
                  <Form.Item
                    name="password"
                    label={t("field.password")}
                    rules={[
                      validators.required(t("field.password")),
                      validators.minLength(8, t("field.password")),
                      validators.validatePasswordStrength(),
                    ]}
                  >
                    <Input.Password
                      max={255}
                      placeholder={t("placeholder.enterYourPassword")}
                    />
                  </Form.Item>
                )}
                {!isEdit && (
                  <Form.Item
                    name="confirmPassword"
                    label={t("field.confirmPassword")}
                    dependencies={["password"]}
                    rules={[
                      validators.required(t("field.confirmPassword")),
                      validators.confirmPasswordMatch(
                        "password",
                        t("validation.passwordNotMatch"),
                        userInfoForm.getFieldValue,
                      ),
                    ]}
                  >
                    <Input.Password
                      max={255}
                      placeholder={t("placeholder.enterYourConfirmPassword")}
                    />
                  </Form.Item>
                )}
              </Col>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label={t("field.email")}
                  rules={[
                    validators.required(t("field.email")),
                    validators.personalEmail(),
                  ]}
                >
                  <Input
                    max={320}
                    placeholder={t("placeholder.enterYourEmail")}
                  />
                </Form.Item>
                <Form.Item
                  name="mobile"
                  label={t("field.mobile")}
                  rules={[
                    validators.required(t("field.mobile")),
                    validators.minLength(9, t("field.mobile")),
                    validators.validateFormatWithRegex(
                      REGEX.NUMERIC,
                      t("field.mobile"),
                    ),
                  ]}
                >
                  <Input
                    max={15}
                    placeholder={t("placeholder.enterYourMobile")}
                  />
                </Form.Item>
                <Form.Item
                  name="role"
                  label={t("field.role")}
                  rules={[validators.required(t("field.role"))]}
                >
                  <Select
                    placeholder={t("placeholder.selectRole")}
                    options={[
                      { value: ROLE_ENUM.ADMIN, label: t("field.admin") },
                      { value: ROLE_ENUM.USER, label: t("field.user") },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item className={"text-end"}>
              <Button type="default" className="m-1" onClick={() => onCancel()}>
                {t("button.cancel")}
              </Button>
              <Button type="primary" htmlType="submit" className="m-1">
                {isEdit ? t("button.save") : t("button.add")}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </ManageContentLayout>
    </>
  );
};

export default AddUserPage;
